import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, UISlotProvider } from '@almadar/ui/context';
import { EventBusProvider, VerificationProvider, NavStackRouterBridge } from '@almadar/ui/providers';
import { UISlotComponent, PageTransition } from '@almadar/ui/components';
import { THEMES, DEFAULT_THEME, DEFAULT_MODE } from './generated/theme-config';
import { I18nProvider, createTranslate } from '@almadar/ui/hooks';
import messages_en from '../../../locales/en.json';
import messages_ar from '../../../locales/ar.json';
import messages_sl from '../../../locales/sl.json';
import { AuthProvider } from './features/auth/AuthContext';
import Login from './features/auth/components/Login';
const LandingENKflowLandingRoute = lazy(() => import('./features/landingenkflowlandingroute/LandingENKflowLandingRoute').then(m => ({ default: m.LandingENKflowLandingRoute })));
const LandingARKflowLandingRoute = lazy(() => import('./features/landingarkflowlandingroute/LandingARKflowLandingRoute').then(m => ({ default: m.LandingARKflowLandingRoute })));
const LandingSLKflowLandingRoute = lazy(() => import('./features/landingslkflowlandingroute/LandingSLKflowLandingRoute').then(m => ({ default: m.LandingSLKflowLandingRoute })));

const NAV_PAGES = [
  { path: '/', name: 'LandingENKflowLandingRoute', orbital: 'LandingEN' },
  { path: '/ar', name: 'LandingARKflowLandingRoute', orbital: 'LandingAR' },
  { path: '/sl', name: 'LandingSLKflowLandingRoute', orbital: 'LandingSL' },
] as const;

// Export queryClient for test access (to clear cache between tests)
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

const I18N = {
  en: { locale: 'en', direction: 'ltr' as const, messages: messages_en, t: createTranslate(messages_en) },
  ar: { locale: 'ar', direction: 'rtl' as const, messages: messages_ar, t: createTranslate(messages_ar) },
  sl: { locale: 'sl', direction: 'ltr' as const, messages: messages_sl, t: createTranslate(messages_sl) },
};
const i18nValue = I18N.en;

function PageTransitionHost({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  return <PageTransition locationKey={pathname}>{children}</PageTransition>;
}

export function App() {
  return (
    <I18nProvider value={i18nValue} documentRoot>
    <ThemeProvider themes={THEMES} defaultTheme={DEFAULT_THEME} defaultMode={DEFAULT_MODE}>
      <QueryClientProvider client={queryClient}>
        <EventBusProvider>
          <VerificationProvider>
          <AuthProvider>
          <UISlotProvider>
            <BrowserRouter>
              <NavStackRouterBridge pages={NAV_PAGES}>
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" /></div>}>
              <PageTransitionHost>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route index element={<I18nProvider value={I18N.en} documentRoot><LandingENKflowLandingRoute /></I18nProvider>} />
                <Route path="ar" element={<I18nProvider value={I18N.ar} documentRoot><LandingARKflowLandingRoute /></I18nProvider>} />
                <Route path="sl" element={<I18nProvider value={I18N.sl} documentRoot><LandingSLKflowLandingRoute /></I18nProvider>} />
                {/* Fallback route - redirect to main page */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              </PageTransitionHost>
              {/* Portal slots for modal, drawer, and toast overlays */}
              <UISlotComponent slot="modal" portal />
              <UISlotComponent slot="drawer" portal />
              <UISlotComponent slot="toast" portal />
              </Suspense>
              </NavStackRouterBridge>
            </BrowserRouter>
          </UISlotProvider>
          </AuthProvider>
          </VerificationProvider>
        </EventBusProvider>
      </QueryClientProvider>
    </ThemeProvider>
    </I18nProvider>
  );
}

export default App;
